// ADENDA z52-2 (Diego, 28-oct-2026; caso SX-2026-0031): «si te ponen que va a aumentar el consumo (por ejemplo "Pondrán 5
// toneladas de aire adicionales") tú hagas una sugerencia con IA de cuánto aumentaría con los equipos adicionales y la
// descripción». Reemplaza «Consumo futuro al año (si lo sabe)» donde aplica (con recibo, en casa, comercio o industria, sin
// ampliación ni dos medidores). La IA propone SOLO los equipos nuevos y su curva de Sonora; la tabla de 12 meses se ajusta (un mes o
// el promedio, con la forma de la curva: logica/consumos-ia.ts) y «Usar este aumento» la guarda con un clic. El motor suma esos
// kWh a cada periodo y recalcula los pesos. Si la IA falla, se enseña el cálculo SIN IA (reglas) con su propio botón: nunca en
// silencio. Verificación del 28-oct: (1) la IA NO se llama sola al abrir (cada apertura gastaba una llamada): solo con el botón, al
// salir de «¿Por qué?» si la descripción cambió, o una vez si se llegó con «Calcular el aumento»; (2) la tabla sin IA SÍ se edita;
// (3) sin IA y sin equipos que las reglas reconozcan, se captura a mano; (4) si lo propuesto se aleja de las reglas (1.5 veces o
// más, o menos de 60 %), se dice antes de usarlo; (5) los 12 meses van en dos renglones de 6 (a 375 px no cabían 12 cifras).
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Check, PencilLine, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { MOTIVO_AUMENTO_MAX, aumentoContraReglasBase, estimarPorReglas, motivoDeAumento, numero } from '@cotizador/compartido';
import type { AumentoAceptado, AumentoConsumo, TipoProyecto } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { Boton, CampoTexto } from '../../componentes/ui.tsx';
import type { FormaCarga } from '../../logica/cuestionario.ts';
import { MESES_TABLA } from '../../logica/consumos-ia.ts';
import { ajustarMesAumento, ajustarPromedioAumento, cargasAMano, cargasDeAumento, debeCalcularAumento, formaDeCargas, mesesDeAumento, resumenAumento, MESES_LARGOS, NOTA_SUPUESTA } from '../../logica/aumento-ia.ts';

/** Lo que propuso la IA (o las reglas) y todavía no se acepta. */
interface Sugerencia {
  cargas: FormaCarga[];
  motivos: string[];
  supuestos: string[];
  fuente: AumentoConsumo['fuente'];
  descripcion: string;
}

export function AumentoConIA({
  tipo,
  giro,
  descripcion,
  aumento,
  consumoFuturoViejo,
  facturacion,
  cotizacionId,
  pedido,
  calcularAlAbrir = false,
  soloBoton = false,
  alUsar,
  alQuitar,
}: {
  /** casa, comercio o industria (la curva y la IA: casa o negocio). */
  tipo: TipoProyecto;
  giro: string | null;
  /** El «¿Por qué?» del cuestionario. */
  descripcion: string;
  /** El aumento ya aceptado (o null). */
  aumento: AumentoAceptado | null;
  /** Cotización de antes con «Consumo futuro al año» (kWh); null si no trae. */
  consumoFuturoViejo: number | null;
  facturacion: 'mensual' | 'bimestral' | null;
  /** La cotización (al editar): el costo de la IA va a su bitácora. */
  cotizacionId: string | null;
  /** Sube cada vez que el vendedor sale del campo «¿Por qué?» (se calcula solo si la descripción cambió). */
  pedido: number;
  /** Se llegó con «Calcular el aumento» de un aviso: calcula UNA vez al abrir (lo pidió el vendedor). */
  calcularAlAbrir?: boolean;
  /** Bloqueo del verificador (29-oct; z55(4)): la tarifa todavía es un SUPUESTO (cuestionario «No sé» y sin recibo). La IA solo se
   *  llama con su botón (nunca sola: ni al salir de «¿Por qué?» ni al abrir) y se dice que en GDBT, GDMTO o GDMTH no se suma. */
  soloBoton?: boolean;
  alUsar: (a: AumentoAceptado) => void;
  alQuitar: () => void;
}) {
  const tipoIA: 'casa' | 'comercio' = tipo === 'casa' ? 'casa' : 'comercio';
  const [sugerencia, setSugerencia] = useState<Sugerencia | null>(null);
  const [motivo, setMotivo] = useState('');
  const [estimando, setEstimando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);
  const [cambiando, setCambiando] = useState(false);
  const [editando, setEditando] = useState<{ campo: string; valor: string } | null>(null);
  // la descripción con que se pidió el último cálculo (para no repetirlo si no cambió). Al abrir cuenta la de la cotización: abrirla
  // no gasta una llamada a la IA.
  const desc = descripcion.trim();
  const ultima = useRef<string | null>(calcularAlAbrir ? null : desc);

  // cálculo sin IA (la regla compartida, igual que la IA simulada): se enseña SOLO si la IA falló
  const sinIA = falla ? estimarPorReglas(desc, tipoIA, { soloNuevo: true }) : null;
  const sugerenciaSinIA: Sugerencia | null = sinIA && sinIA.cargas.length ? { cargas: formaDeCargas(sinIA.cargas.map((c) => c.carga)), motivos: sinIA.cargas.map((c) => c.motivo), supuestos: sinIA.supuestos, fuente: 'reglas', descripcion: desc } : null;

  async function calcular() {
    if (desc.length < 3 || estimando) return;
    ultima.current = desc;
    setEstimando(true);
    setFalla(null);
    try {
      const r = await api.estimarAumento(desc, tipoIA, giro, cotizacionId);
      const cargas = formaDeCargas(r.cargas);
      setSugerencia({ cargas, motivos: r.motivos, supuestos: r.supuestos, fuente: r.fuente ?? 'ia', descripcion: desc });
      setMotivo(r.motivo ?? motivoDeAumento(r.cargas, MOTIVO_AUMENTO_MAX));
    } catch (e) {
      setSugerencia(null);
      setFalla(e instanceof Error ? e.message : String(e));
      const reglas = estimarPorReglas(desc, tipoIA, { soloNuevo: true });
      setMotivo(motivoDeAumento(reglas.cargas.map((c) => c.carga), MOTIVO_AUMENTO_MAX));
    } finally {
      setEstimando(false);
    }
  }

  // solo: al salir de «¿Por qué?» con otra descripción (pedido) o, una vez, si se llegó con «Calcular el aumento». Con la tarifa
  // supuesta (soloBoton), nunca: solo con el botón.
  useEffect(() => {
    if (!soloBoton && (pedido > 0 || calcularAlAbrir) && debeCalcularAumento({ descripcion: desc, aceptado: aumento, ultima: ultima.current, viejo: calcularAlAbrir ? null : consumoFuturoViejo })) void calcular();
    // (calcular lee el estado de este render; solo se dispara con el pedido o al montar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido]);

  function usar(s: Sugerencia) {
    const a = cargasDeAumento(tipo, s.cargas, { descripcion: s.descripcion, fuente: s.fuente, motivo: motivo.trim() || motivoDeAumento(s.cargas.map((c) => ({ equipo: c.equipo })), MOTIVO_AUMENTO_MAX) });
    if (!a) {
      setFalla('El aumento suma 0 kWh: revisa la tabla.');
      return;
    }
    alUsar(a);
    setSugerencia(null);
    setFalla(null);
    setCambiando(false);
  }

  // ── Ya aceptado (y no lo está cambiando)
  if (aumento && !cambiando) {
    const otra = aumento.descripcion.trim() !== desc && desc.length >= 3;
    return (
      <section className="estimar-ia" aria-label="Aumento de consumo">
        <div className="fila-botones fila-botones--entre">
          <span role="status">
            Aumento: <strong>+{numero(aumento.kwh_mes.reduce((x, y) => x + y, 0))} kWh al año</strong> ({aumento.motivo}; {aumento.fuente === 'reglas' ? 'sin IA, con reglas' : aumento.fuente === 'vendedor' ? 'lo escribiste tú' : 'IA'}).
          </span>
          <span className="fila-botones">
            <Boton chico variante="secundario" onClick={() => (setCambiando(true), setSugerencia({ cargas: formaDeCargas(aumento.cargas), motivos: [], supuestos: [], fuente: aumento.fuente, descripcion: aumento.descripcion }), setMotivo(aumento.motivo))}>
              Cambiar
            </Boton>
            <Boton chico variante="fantasma" icono={<Trash2 aria-hidden />} onClick={alQuitar}>
              Quitar
            </Boton>
          </span>
        </div>
        {soloBoton && <p className="nota-suave">{NOTA_SUPUESTA}</p>}
        {otra && (
          <div className="fila-botones fila-botones--entre">
            <span className="texto-ambar">La descripción cambió: el aumento se calculó con «{aumento.descripcion.slice(0, 80)}».</span>
            <Boton chico icono={<RefreshCw aria-hidden />} onClick={() => (setCambiando(true), void calcular())}>
              Volver a calcular
            </Boton>
          </div>
        )}
      </section>
    );
  }

  const viva = sugerencia ?? null;
  // sin IA y sin equipos que las reglas reconozcan («Van a ampliar el local»): a mano, con la misma tabla
  const aMano = () => setSugerencia({ cargas: cargasAMano(), motivos: [], supuestos: ['Captura a mano: escribe el promedio al mes de kWh DE MÁS (o mes por mes).'], fuente: 'vendedor', descripcion: desc });
  return (
    <section className="estimar-ia" aria-label="Aumento de consumo">
      {consumoFuturoViejo !== null && !viva && (
        <p className="nota-suave">Usa el consumo futuro de antes ({numero(consumoFuturoViejo)} kWh al año, solo para la Holgada). Calcula el aumento con IA para que las opciones, el ahorro y el retorno lo cuenten.</p>
      )}
      <div className="fila-botones fila-botones--entre">
        <span className="nota-suave">{desc.length < 3 ? 'Escribe arriba qué equipos va a poner (toneladas de aire, refrigeradores, máquinas…).' : viva ? 'Revisa la sugerencia y úsala.' : soloBoton ? `Si quieres, la IA estima cuánto sube con los equipos nuevos. ${NOTA_SUPUESTA}` : 'La IA estima cuánto sube con los equipos nuevos.'}</span>
        <Boton variante={viva ? 'secundario' : 'principal'} icono={<Sparkles aria-hidden />} onClick={() => void calcular()} disabled={desc.length < 3 || estimando}>
          {estimando ? 'Calculando…' : consumoFuturoViejo !== null && !viva ? 'Calcular el aumento con IA' : viva ? 'Volver a calcular (IA)' : 'Calcular cuánto sube (IA)'}
        </Boton>
      </div>
      {falla && (
        <div className="aviso-ancho aviso-ancho--rojo" role="alert">
          La IA no pudo calcularlo: {falla.replace(/\.$/, '')}.
        </div>
      )}
      {/* lo que el vendedor ajusta en la tabla ya es suyo: fuente «vendedor» (el motor lo dice así y lo mide contra las reglas) */}
      {viva && <TablaAumento s={viva} tipo={tipo} facturacion={facturacion} motivo={motivo} setMotivo={setMotivo} editando={editando} setEditando={setEditando} alCambiar={(cargas) => setSugerencia({ ...viva, cargas, fuente: 'vendedor' })} alUsar={() => usar(viva)} boton="Usar este aumento" />}
      {!viva && sugerenciaSinIA && (
        <>
          <p className="nota-suave">Cálculo SIN IA (reglas sencillas, a la vista): úsalo solo si te parece bien.</p>
          <TablaAumento s={sugerenciaSinIA} tipo={tipo} facturacion={facturacion} motivo={motivo} setMotivo={setMotivo} editando={editando} setEditando={setEditando} alCambiar={(cargas) => setSugerencia({ ...sugerenciaSinIA, cargas, fuente: 'vendedor' })} alUsar={() => usar(sugerenciaSinIA)} boton="Usar este (sin IA)" />
        </>
      )}
      {!viva && falla && !sugerenciaSinIA && desc.length >= 3 && (
        <div className="fila-botones fila-botones--entre">
          <span className="nota-suave">Sin IA, las reglas no reconocen equipos en «{desc.slice(0, 60)}»: captúralo a mano.</span>
          <Boton variante="secundario" icono={<PencilLine aria-hidden />} onClick={aMano}>
            Capturarlo a mano
          </Boton>
        </div>
      )}
      {cambiando && aumento && (
        <div className="fila-botones">
          <Boton chico variante="fantasma" onClick={() => (setCambiando(false), setSugerencia(null))}>
            Dejar el aumento de antes
          </Boton>
        </div>
      )}
    </section>
  );
}

function TablaAumento({
  s,
  tipo,
  facturacion,
  motivo,
  setMotivo,
  editando,
  setEditando,
  alCambiar,
  alUsar,
  boton,
}: {
  s: Sugerencia;
  tipo: TipoProyecto;
  facturacion: 'mensual' | 'bimestral' | null;
  motivo: string;
  setMotivo: (m: string) => void;
  editando: { campo: string; valor: string } | null;
  setEditando: (e: { campo: string; valor: string } | null) => void;
  alCambiar: (cargas: FormaCarga[]) => void;
  alUsar: () => void;
  boton: string;
}) {
  const porMes = mesesDeAumento(s.cargas);
  const r = resumenAumento(porMes, facturacion);
  const alto = Math.max(1, ...porMes);
  // verificación del 28-oct: lo propuesto contra las reglas con la misma descripción (la misma cuenta que el aviso del motor)
  const contra = aumentoContraReglasBase({ kwh_mes: porMes, descripcion: s.descripcion, fuente: s.fuente }, tipo === 'casa' ? 'casa' : 'comercio');
  const valorDe = (campo: string, x: number) => (editando?.campo === campo ? editando.valor : String(Math.round(x)));
  function aplicar(campo: string, actual: number) {
    if (!editando || editando.campo !== campo) return;
    const v = Number(editando.valor.replace(/,/g, '').trim());
    setEditando(null);
    if (!(v > 0) || v === Math.round(actual)) return;
    alCambiar(campo === 'promedio' ? ajustarPromedioAumento(s.cargas, v) : ajustarMesAumento(s.cargas, Number(campo), v));
  }
  const entrada = (campo: string, x: number, etiqueta: string, className: string) => ({
    className,
    inputMode: 'numeric' as const,
    'aria-label': etiqueta,
    value: valorDe(campo, x),
    onChange: (e: { target: { value: string } }) => setEditando({ campo, valor: e.target.value }),
    onBlur: () => aplicar(campo, x),
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') e.currentTarget.blur();
    },
  });
  const motivoMal = motivo.trim().length < 3 || motivo.trim().length > MOTIVO_AUMENTO_MAX;
  return (
    <>
      <p role="status">
        {s.fuente === 'reglas' ? 'Sin IA sale' : 'La IA sugiere'} <strong>+{numero(r.anual)} kWh al año</strong> (en promedio +{numero(r.promedioMes)} al mes{r.porPeriodo ? `, +${numero(r.porPeriodo.valor)} ${r.porPeriodo.al}` : ''}; {MESES_LARGOS[r.mesAlto]} +{numero(r.alto)}).
      </p>
      <p className="nota-suave">
        Lo que entendió: {s.cargas.map((c) => `${c.equipo}, ${c.horas_dia} h al día${Number(c.meses_uso) < 12 ? `, ${c.meses_uso} meses` : ''}`).join('; ')}. Estimado ±15 %.
      </p>
      {contra?.nivel && (
        <p className={contra.nivel === 'rojo' ? 'aviso-ancho aviso-ancho--rojo' : 'aviso-ancho aviso-ancho--ambar'} role="note">
          Ojo: es {contra.razon >= 1 ? `${numero(Math.round(contra.razon * 10) / 10)} veces lo` : `${numero(Math.round(contra.razon * 100))} % de lo`} que dan las reglas con esta descripción (+{numero(contra.reglas)} kWh al año). Revisa los equipos y las horas antes de usarlo.
        </p>
      )}
      <div className="aumento-ia__meses" role="group" aria-label="Aumento mes por mes (kWh de más)">
        {porMes.map((k, i) => (
          <div className="aumento-ia__mes" key={MESES_TABLA[i]}>
            <span className="aumento-ia__nombre">{MESES_TABLA[i]}</span>
            <span className="aumento-ia__caja" aria-hidden>
              <span className="estimar-ia__barra" style={{ blockSize: `${Math.round((k / alto) * 100)}%` }} />
            </span>
            <input {...entrada(String(i), k, `kWh de más en ${MESES_TABLA[i]}`, 'estimar-ia__entrada aumento-ia__entrada')} />
          </div>
        ))}
      </div>
      <div className="estimar-ia__resumen">
        <label className="estimar-ia__promedio">
          <span>Promedio al mes</span>
          <input {...entrada('promedio', r.anual / 12, 'Aumento promedio al mes (kWh)', 'estimar-ia__entrada')} />
          <span>kWh</span>
        </label>
      </div>
      {s.supuestos.length > 0 && (
        <ul className="ajuste-consumos__supuestos">
          {s.supuestos.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      <CampoTexto
        etiqueta="Cómo lo dice la propuesta"
        maxLength={MOTIVO_AUMENTO_MAX}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        ayuda={`Va a media frase: «ya sumamos ${motivo.trim() || '…'}». Hasta ${MOTIVO_AUMENTO_MAX} letras.`}
        error={motivoMal ? `De 3 a ${MOTIVO_AUMENTO_MAX} letras.` : null}
      />
      <div className="fila-botones fila-botones--entre">
        <span className="nota-suave">Al usarlo, las opciones, el ahorro y el retorno se calculan con el consumo nuevo.</span>
        <Boton variante="principal" icono={<Check aria-hidden />} onClick={alUsar} disabled={motivoMal || !(r.anual > 0)}>
          {boton}
        </Boton>
      </div>
    </>
  );
}
